import React, { useEffect, useState } from "react";
import { BadgeCheck, CalendarClock, AlertTriangle, FileText } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, Avatar, EmptyState, fmtDate } from "../components/ui";

const STAT_TONE = { CONFIRMED: "ok", ON_PROBATION: "info", EXTENDED: "warn" };

export default function Confirmation() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [extend, setExtend] = useState(null); // employee row
  const [days, setDays] = useState(90);
  const [busy, setBusy] = useState(false);
  const canManage = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = () => api.get("/confirmation").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const confirm = async (r) => {
    if (!window.confirm(`Confirm ${r.name}? This generates a Confirmation Letter and marks them CONFIRMED.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/confirmation/${r.employeeId}/confirm`);
      await load();
      if (data.letterId && window.confirm(`Confirmed. Letter ${data.letterNumber} generated. Open the PDF now?`)) openLetter(data.letterId);
    } catch (e) { window.alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const submitExtend = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/confirmation/${extend.employeeId}/extend`, { days: +days }); setExtend(null); setDays(90); load(); }
    catch (err) { window.alert(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const openLetter = async (lid) => {
    try { const res = await api.get(`/documents/letter/${lid}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank"); } catch { window.alert("Could not open letter"); }
  };

  if (!rows) return <Loading />;
  const counts = rows.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; if (r.overdue) a.overdue++; return a; }, { overdue: 0 });
  const stat = [
    { label: "On Probation", value: counts.ON_PROBATION || 0, icon: CalendarClock, tone: "text-blue-600" },
    { label: "Extended", value: counts.EXTENDED || 0, icon: CalendarClock, tone: "text-amber-600" },
    { label: "Confirmed", value: counts.CONFIRMED || 0, icon: BadgeCheck, tone: "text-emerald-600" },
    { label: "Overdue", value: counts.overdue || 0, icon: AlertTriangle, tone: "text-red-600" },
  ];

  return (
    <div>
      <PageHeader title="Confirmation & Probation" subtitle="Track probation periods and confirm employees" />
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stat.map((s) => (
          <Card key={s.label} className="p-5" data-testid={`conf-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between"><span className="text-sm font-medium text-slate-500 dark:text-slate-400">{s.label}</span><s.icon size={18} className={s.tone} /></div>
            <p className="mt-2 font-head text-3xl font-extrabold text-slate-900 dark:text-white">{s.value}</p>
          </Card>
        ))}
      </div>
      {rows.length === 0 ? <EmptyState icon={BadgeCheck} title="No employees on probation" /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full" data-testid="confirmation-table">
            <thead><tr><th className="th">Employee</th><th className="th">Joined</th><th className="th">Probation End</th><th className="th">Status</th><th className="th">Letter</th>{canManage && <th className="th">Action</th>}</tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((r) => (
                <tr key={r.employeeId} data-testid={`conf-row-${r.employeeId}`}>
                  <td className="td">
                    <div className="flex items-center gap-3"><Avatar name={r.name} size={34} />
                      <div><p className="font-medium text-slate-800 dark:text-slate-100">{r.name}</p>{r.designation && <p className="text-xs text-slate-400">{r.designation}</p>}</div>
                    </div>
                  </td>
                  <td className="td text-sm">{fmtDate(r.dateOfJoining)}</td>
                  <td className="td text-sm">
                    <span className={r.overdue ? "font-semibold text-red-600" : ""}>{fmtDate(r.probationEnd)}</span>
                    {r.overdue && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700 dark:bg-red-500/15">OVERDUE</span>}
                  </td>
                  <td className="td"><Badge tone={STAT_TONE[r.status]}>{r.status.replace("_", " ")}</Badge></td>
                  <td className="td">{r.letterId ? <button data-testid={`conf-letter-${r.employeeId}`} onClick={() => openLetter(r.letterId)} className="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline"><FileText size={13} /> {r.letterNumber}</button> : <span className="text-xs text-slate-400">—</span>}</td>
                  {canManage && <td className="td">
                    {r.status === "CONFIRMED" ? <span className="text-xs text-slate-400">—</span> : (
                      <div className="flex gap-1.5">
                        <button data-testid={`conf-confirm-${r.employeeId}`} disabled={busy} onClick={() => confirm(r)} className="rounded bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 disabled:opacity-50 dark:bg-emerald-500/15">Confirm</button>
                        <button data-testid={`conf-extend-${r.employeeId}`} disabled={busy} onClick={() => { setExtend(r); setDays(90); }} className="rounded bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700 disabled:opacity-50 dark:bg-amber-500/15">Extend</button>
                      </div>
                    )}
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}
      <Modal open={!!extend} onClose={() => setExtend(null)} title={`Extend probation — ${extend?.name || ""}`}
        footer={<><button onClick={() => setExtend(null)} className="btn-outline">Cancel</button><button form="ext-form" disabled={busy} className="btn-accent">Extend</button></>}>
        <form id="ext-form" onSubmit={submitExtend} className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">Current probation ends {fmtDate(extend?.probationEnd)}. Choose how many days to extend.</p>
          <div><label className="label">Extension (days)</label>
            <select data-testid="ext-days" value={days} onChange={(e) => setDays(e.target.value)} className="input">
              {[30, 60, 90, 120, 180].map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}
